// src/components/repasses/RepassesTab.tsx

import { Alert, Box, Collapse, Paper, Typography } from "@mui/material";
import { saveAs } from "file-saver";
import { useState } from "react";
import * as XLSX from "xlsx";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { fetchCommissionData } from "../../hooks/fetchCommissionData"; // Hook de busca de comissão
import { handleCompareRepasse } from "../../hooks/handleCompareRepasse"; // NOVO: Hook de Comparação
import { handleValidateRepasse } from "../../hooks/handleValidateRepasse";
import { useExcelImport } from "../../hooks/useExcelImport";
import { headersRepasses, REQUIRED_HEADERS_REPASSE, RowDataRepasse } from "../../types";
import { RepasseDataTable } from "../RepasseDataTable";
import { ToolbarToolsRepasse } from "../ToolbarToolsRepasse";

export const RepassesTab = () => {
	const toast = useToast();
	const [data, setData] = useState<RowDataRepasse[]>([]);
	const [missingFields, setMissingFields] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [fileInfo, setFileInfo] = useState<{ name: string; rowCount: number; date: string } | null>(null);
	const [dateFilter, setDateFilter] = useState("current_month");

	let currentFileName = "";

	// LÓGICA DE VERIFICAÇÃO E ANÁLISE (Executada pelo botão "Verificar")
	const handleAnalyze = async (filter: string) => {
		if (!data.length) {
			toast.present({ type: "warning", messageOrContent: "Importe um arquivo antes de verificar." });
			return;
		}
		setLoading(true);

		// 1. Validação Local
		let validatedData = handleValidateRepasse(data);
		// Não atualizamos o estado aqui, pois queremos mostrar o resultado após a API

		// 2. Consulta Assíncrona de Comissão (Busca ID e Detalhes)
		const commissionCheckedData = await fetchCommissionData(validatedData, toast, filter);

		// 3. CRUZAMENTO DE DADOS E CONFERÊNCIA FINAL (handleCompareRepasse)
		const { updatedData, newApiItems } = handleCompareRepasse(commissionCheckedData);

		// 4. Combina os dados (linhas do CSV + novas linhas da API)
		const finalData = [...updatedData, ...newApiItems];

		setData(finalData); // Define o estado final

		setLoading(false);

		// Mensagem de sucesso/erro final
		const errors = finalData.filter((row) => row.status === "inválido").length;
		const success = finalData.filter((row) => row.status === "validado").length;

		toast.present({
			type: errors > 0 ? "warning" : "success",
			messageOrContent: `${success} linhas conferidas. ${newApiItems.length} novos itens adicionados. ${errors} linhas com divergências ou faltantes.`,
		});
	};

	// LÓGICA DE IMPORTAÇÃO (Reutilizada)
	const { handleFile } = useExcelImport({
		requiredHeaders: REQUIRED_HEADERS_REPASSE,
		limit: 1000,
		onStart: () => {
			setLoading(true);
			setData([]);
			setMissingFields([]);
			setFileInfo(null);
		},
		onFinish: () => setLoading(false),
		onSuccess: (processed) => {
			if (processed.length >= 100) {
				toast.present({ type: "warning", messageOrContent: "Importação limitada a 100 linhas. Linhas adicionais foram ignoradas." });
			}
			const indexed = processed.map((row, index) => ({
				...row,
				RowNumber: index,
				status: "importado",
				mensagem: "",
			})) as RowDataRepasse[];

			setData(indexed);

			setFileInfo({
				name: currentFileName,
				rowCount: indexed.length,
				date: new Date().toLocaleString(),
			});
		},
		onError: (missing) => {
			toast.present({ type: "error", messageOrContent: "Campos obrigatórios ausentes" });
			setMissingFields(missing);
		},
	});

	// FUNÇÕES DE DOWNLOAD E EXPORTAÇÃO (Reutilizadas)
	const handleDownloadModelCSV = () => {
		const csv = headersRepasses.join(";") + "\n";
		saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "modelo_repasse.csv");
	};

	const handleExportToExcel = () => {
		if (!data.length) return;
		const exportData = data.map(({ RowNumber, ...rest }) => rest);
		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, "Resultado Repasse");
		XLSX.writeFile(workbook, "resultado_repasse.xlsx");
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={2} mx={1}>
			{loading && <LoadingComp isopen={loading} />}

			{fileInfo && (
				<Typography variant="body2" color="text.secondary">
					Arquivo: <strong>{fileInfo.name}</strong> | Linhas: <strong>{fileInfo.rowCount}</strong> | Importado em: <strong>{fileInfo.date}</strong>
				</Typography>
			)}

			<ToolbarToolsRepasse
				onFileImport={(file) => {
					currentFileName = file.name;
					handleFile(file);
				}}
				onAnalyze={() => handleAnalyze(dateFilter)}
				onDownloadModel={handleDownloadModelCSV}
				onExportResult={handleExportToExcel}
				dateFilter={dateFilter}
				onDateFilterChange={setDateFilter}
			/>

			<Collapse in={missingFields.length > 0}>
				<Alert severity="error">
					Campos obrigatórios ausentes: <strong>{missingFields.join(", ")}</strong>
				</Alert>
			</Collapse>

			<RepasseDataTable data={data} />

			{!data.length && !missingFields.length && (
				<Typography variant="body2" color="text.secondary">
					Nenhum dado importado ainda.
				</Typography>
			)}
		</Box>
	);
};
